import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import classNames from 'classnames';
import { Spinner, TabFilter, TRWrapper } from '../common/Helper';
import { formatSecs, paginatedFetch } from '../../lib/utils';
import BurnDown from '../perf/BurnDown';
import IssueGroup from '../perf/IssueGroup';
import IssuesTable from '../widgets/IssuesTable';
import IssueBoard from '../pages/IssueBoard';
import { useTicketFetcher, useProjectNames } from '../../lib/hooks';

const TagCloud = ({ tags, tagStatus, onTagClick }) => {
    let list = [];
    for (var name in tags) {
        const active = Boolean(tagStatus[name]);
        list.push(
            <button
                key={name}
                type="button"
                onClick={() => onTagClick(name)}
                className={classNames('tag-chip', { 'is-active': active })}
            >
                {name}
                <span className="tag-chip__n">{tags[name].tasks}</span>
                <span className="tag-chip__n is-time">{formatSecs(tags[name].spent)}</span>
            </button>
        );
    }
    list = list.sort((a, b) => a.key.localeCompare(b.key));
    return <div className="tag-cloud">{list}</div>;
};

const TimePersonTable = ({ total, people, error, message }) => {
    const list = [];
    for (var name in people) {
        list.push(
            <tr key={name}>
                <td>{name}</td>
                <td className="ds-nums">{formatSecs(people[name].totalSpent)} / {formatSecs(people[name].totalEstimate)}</td>
            </tr>
        );
    }

    if (error) {
        list.push(<TRWrapper key="error" colSpan="2"><div className="issue-empty" style={{ color: 'var(--ds-danger)' }}>{error}</div></TRWrapper>);
    } else if (message) {
        list.push(<TRWrapper key="message" colSpan="2"><div className="issue-empty">{message}</div></TRWrapper>);
    } else if (list.length === 0) {
        list.push(<TRWrapper key="loading" colSpan="2"><Spinner /></TRWrapper>);
    }

    return (
        <div className="ds-panel">
            <div className="ds-panel__head">
                <h3 className="ds-panel__title">Time by person</h3>
                <span className="ds-panel__sub ds-nums">{formatSecs(total)}</span>
            </div>
            <div className="ds-table-wrap">
                <table className="ds-table">
                    <thead>
                        <tr>
                            <th>Person</th>
                            <th>Spent / Estimate</th>
                        </tr>
                    </thead>
                    <tbody>{list}</tbody>
                </table>
            </div>
        </div>
    );
};

const Project = () => {
    const [sp] = useSearchParams();
    const { project } = useParams();
    const projectNames = useProjectNames();
    const projectName = projectNames[project] || project;

    const error = null;

    const [tagStatus, setTagStatus] = useState({});
    const [issues, setIssues] = useState(null);
    const [total, setTotal] = useState(0);
    const [tags, setTags] = useState([]);
    const [people, setPeople] = useState({});

    const { ticketListFiltered } = useTicketFetcher({ project });
    const message = issues && issues.length === 0
        ? `No tickets scheduled into the current sprint for ${projectName}.`
        : null;

    const [backlogIssues, setBacklogIssues] = useState(null);
    const [backlogError, setBacklogError] = useState(null);

    const tab = sp.get('tab') || 'list';

    useEffect(() => {
        setBacklogIssues(null);
        setBacklogError(null);
    }, [project]);

    useEffect(() => {
        if (tab !== 'backlog' || backlogIssues !== null) {
            return;
        }
        paginatedFetch('/api/v1/tickets/list', {
            project,
            mode: 'normal',
            milestone: 'all',
        }).then(res => {
            setBacklogIssues(res.data);
        }).catch(ex => {
            setBacklogError(ex.message);
        });
    }, [tab, project, backlogIssues]);

    useEffect(() => {
        let newList = ticketListFiltered;
        if (!newList) {
            return;
        }
        newList = newList.filter(item => item.project.name === project);
        const tList = newList.reduce((acc, item) => {
            item.labels.forEach(label => {
                if (!acc[label]) {
                    acc[label] = { tasks: 0, spent: 0 };
                }
                acc[label].tasks++;
                acc[label].spent += item.timeSpent;
            });
            return acc;
        }, {});

        setTags(tList);
        if (Object.keys(tagStatus).length !== 0) {
            newList = newList.filter(item => item.labels.some(s => tagStatus[s]));
        }

        newList = newList.sort((a, b) => {
            const abase = a.state === 'closed' ? 1000 * 86400 : 0;
            const bbase = b.state === 'closed' ? 1000 * 86400 : 0;
            return (abase) - (bbase);
        });

        const t = newList.reduce((acc, item) => acc + parseInt(item.timeSpent), 0);
        setTotal(t);
        const map = newList.reduce((acc, item) => {
            if (!item.assignee) {
                return acc;
            }
            if (!acc[item.assignee.name]) {
                acc[item.assignee.name] = new IssueGroup(item.assignee.username, item.assignee.name);
            }
            acc[item.assignee.name].addIssue(item);
            return acc;
        }, {});

        setPeople(map);
        setIssues(newList);
    }, [ticketListFiltered, tagStatus]);

    const onTagClick = (name) => {
        if (tagStatus[name]) {
            delete tagStatus[name];
        } else {
            tagStatus[name] = true;
        }
        setTagStatus(Object.assign({}, tagStatus));
    };

    const tabs = ["List", "Board", "Backlog"];

    return (
        <div>
            <div className="page-head" style={{ justifyContent: 'space-between' }}>
                <div>
                    <h2>{projectName}</h2>
                    <span className="ds-badge is-accent">Project</span>
                </div>
                <Link to={`/poker/${project}`} className="ds-btn is-soft">Planning Poker</Link>
            </div>

            <div className="proj-grid">
                <div>{issues && <BurnDown issueList={issues} />}</div>
                <div className="proj-side">
                    <TimePersonTable error={error} message={message} people={people} total={total} />
                    <TagCloud tags={tags} tagStatus={tagStatus} onTagClick={onTagClick} />
                </div>
            </div>

            <div className="page-head" style={{ marginBottom: 'var(--ds-space-2)' }}>
                <h3>Issues <span className="ds-nums" style={{ color: 'var(--ds-ink-muted)', fontWeight: 400 }}>{issues?.length || 0}</span></h3>
            </div>
            <TabFilter name="tab" filters={tabs} />
            <div style={{ marginTop: 'var(--ds-space-4)' }}>
                {tab === 'board' && <IssueBoard tagStatus={tagStatus} filter={{ project }} />}
                {tab === 'backlog' && <IssuesTable tagStatus={{}} error={backlogError} issues={backlogIssues} />}
                {tab === 'list' && <IssuesTable tagStatus={tagStatus} error={error} issues={issues} />}
            </div>
        </div>
    );
};

export default Project;
